import { animate, animation, style, transition, trigger, useAnimation } from "@angular/animations";
import { DOCUMENT } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgZone, Optional, PLATFORM_ID, Renderer2, SkipSelf, ViewEncapsulation } from "@angular/core";
import { PrimeNGConfig } from "primeng/api";
import { DynamicDialogComponent, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { EditDialogInstance } from "../editDialog/editDialogService";

export enum ConfirmDialogEventType {
    CONFIRM = 'CONFIRM',
    REJECT = 'REJECT',
  }

export enum ConfirmDialogSeverity {
    DEFAULT = 'DEFAULT',
    WARN = 'WARN'
}

const showAnimation = animation([style({ transform: '{{transform}}', opacity: 0 }), animate('{{transition}}', style({ transform: 'none', opacity: 1 }))]);
const hideAnimation = animation([animate('{{transition}}', style({ transform: '{{transform}}', opacity: 0 }))]);

@Component({
    selector: 'nx-confirmDialog',
    template: `
        <div
            #mask
            [ngClass]="{
                'p-dialog-mask': true,
                'p-component-overlay p-component-overlay-enter p-dialog-mask-scrollblocker': config.modal !== false,
                'p-dialog-left': position === 'left',
                'p-dialog-right': position === 'right',
                'p-dialog-top': position === 'top',
                'p-dialog-bottom': position === 'bottom',
                'p-dialog-top-left': position === 'topleft' || position === 'top-left',
                'p-dialog-top-right': position === 'topright' || position === 'top-right',
                'p-dialog-bottom-left': position === 'bottomleft' || position === 'bottom-left',
                'p-dialog-bottom-right': position === 'bottomright' || position === 'bottom-right'
            }"
            [class]="config.maskStyleClass"
        >
            <div
                #container
                [ngClass]="{ 'p-dialog p-dynamic-dialog p-component': true, 'p-dialog-rtl': config.rtl, 'p-dialog-resizable': config.resizable, 'p-dialog-draggable': config.draggable, 'p-dialog-maximized': maximized }"
                [ngStyle]="config.style"
                [class]="config.styleClass"
                [@animation]="{ value: 'visible', params: { transform: transformOptions, transition: config.transitionOptions || '150ms cubic-bezier(0, 0, 0.2, 1)' } }"
                (@animation.start)="onAnimationStart($event)"
                (@animation.done)="onAnimationEnd($event)"
                role="dialog"
                *ngIf="visible"
                [style.width]="config.width"
                [style.height]="config.height"
                [attr.aria-labelledby]="ariaLabelledBy"
                [attr.aria-modal]="true"
            >
                <div *ngIf="config.resizable" class="p-resizable-handle" style="z-index: 90;" (mousedown)="initResize($event)"></div>
                <div #titlebar class="p-dialog-header" (mousedown)="initDrag($event)" *ngIf="config.showHeader === false ? false : true">
                    <span class="p-dialog-title" [id]="ariaLabelledBy + '_title'">{{ config.header ? (config.header | translate) : 'Aktion bestätigen' }}</span>
                    <div class="p-dialog-header-icons">
                        <!-- no icons -->
                    </div>
                </div>
                <div #content class="p-dialog-content" [ngStyle]="config.contentStyle">
                    {{message | translate}}
                </div>
                <div class="p-dialog-footer flex flex-row justify-end">
                    <nx-default-button
                        class="reject"
                        [label]="cancelLabel | translate"
                        (click)="reject()">
                    </nx-default-button>
                    <nx-default-button
                        class="confirm ml-2"
                        [ngClass]="getClassBySeverity(severity)"
                        [label]="confirmLabel | translate" 
                        (click)="confirm()">
                    </nx-default-button>
                </div>
            </div>
        </div>
    `,
    animations: [trigger('animation', [transition('void => visible', [useAnimation(showAnimation)]), transition('visible => void', [useAnimation(hideAnimation)])])],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['confirmDialog.component.scss'],
    host: {
        class: 'p-element nx-cofnirm-dialog nx-dialog'
    }
})
export class NxConfirmDialogComponent extends DynamicDialogComponent {

    confirmDisabled: boolean = true;
    confirmDialogInstance: EditDialogInstance;
    ownDialogRef: DynamicDialogRef;
    public message: string;
    public cancelLabel: string;
    public confirmLabel: string;
    public severity: ConfirmDialogSeverity;

    constructor(
        @Inject(DOCUMENT) document: Document,
        @Inject(PLATFORM_ID) platformId: any,
        cd: ChangeDetectorRef,
        renderer: Renderer2,
        config: DynamicDialogConfig,
        dialogRef: DynamicDialogRef,
        zone: NgZone,
        primeNGConfig: PrimeNGConfig,
        @SkipSelf() @Optional() parentDialog: DynamicDialogComponent
    ) {
        
        super(document, platformId, cd, renderer, config, dialogRef, zone, primeNGConfig, parentDialog);
        this.ownDialogRef = dialogRef;
    }

    reject(){
        this.ownDialogRef.close(ConfirmDialogEventType.REJECT);
    }

    confirm(){
        this.ownDialogRef.close(ConfirmDialogEventType.CONFIRM);
    }

    getClassBySeverity(severity: ConfirmDialogSeverity){
        return severity === ConfirmDialogSeverity.WARN ? 'warn' : '';
    }

}